import React, { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import AvatarUpload from "@/components/AvatarUpload";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [picture, setPicture] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setPhone(user.phone || "");
    setAddress(user.address || "");
    setPicture(user.picture || null);
  }, [user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await updateProfile({
      name: name.trim() || null,
      phone: phone.trim(),
      address: address.trim(),
      picture,
    });
    setSaving(false);
    if (res.ok) {
      toast.success("Profile updated");
    } else {
      toast.error(res.error || "Could not save profile");
    }
  };

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="label-eyebrow text-[#002FA7]">Profile</div>
        <h1 className="text-4xl tracking-tight font-black text-[#111827]">Affiliate marketer profile</h1>
        <p className="text-[#4B5563] mt-2">Customers see your name on every link they click. Keep your details up to date.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <form onSubmit={onSubmit} className="lg:col-span-3 card-flat p-6 space-y-6" data-testid="profile-form">
          <div>
            <label className="label-eyebrow text-[#4B5563] block mb-3">Profile picture</label>
            <AvatarUpload value={picture} onChange={setPicture} name={name} testid="profile-avatar" />
            <p className="text-xs text-[#9CA3AF] mt-2">Square images work best. Auto-compressed to 512px before upload.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow text-[#4B5563]">Full name</label>
              <input data-testid="profile-name-input" required className="input-base mt-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ada Lovelace" />
            </div>
            <div>
              <label className="label-eyebrow text-[#4B5563]">Phone</label>
              <input data-testid="profile-phone-input" type="tel" className="input-base mt-2" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+234 801 555 0199" />
            </div>
          </div>

          <div>
            <label className="label-eyebrow text-[#4B5563]">Address</label>
            <input data-testid="profile-address-input" className="input-base mt-2" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, city, country" />
          </div>

          <div>
            <label className="label-eyebrow text-[#4B5563]">Email</label>
            <input className="input-base mt-2 bg-[#F9FAFB] text-[#9CA3AF]" value={user.email} readOnly />
            <p className="text-xs text-[#9CA3AF] mt-1">Email is locked to your account.</p>
          </div>

          <div className="flex items-center justify-between border-t border-[#E5E7EB] pt-4">
            <div className="text-xs text-[#4B5563]">
              Account: <span className="font-bold text-[#111827]">{user.auth_provider === "google" ? "Google" : "Email & password"}</span> · Joined {new Date(user.created_at).toLocaleDateString()}
            </div>
            <button type="submit" disabled={saving} data-testid="profile-save-button" className="btn-primary">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>

        <aside className="lg:col-span-2 space-y-4">
          <div className="card-flat p-6">
            <div className="label-eyebrow text-[#002FA7] mb-4">Public preview</div>
            <div className="flex items-center gap-4">
              {picture ? (
                <img src={picture} alt={name} className="h-20 w-20 rounded-full object-cover border border-[#E5E7EB]" />
              ) : (
                <div className="h-20 w-20 rounded-full bg-[#111827] text-white flex items-center justify-center font-display font-black text-3xl">
                  {(name || "U").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-display font-extrabold text-xl truncate">{name || "Your name"}</div>
                <div className="text-sm text-[#4B5563] truncate">{user.email}</div>
                {phone && <div className="text-xs text-[#4B5563] mt-1 font-mono">{phone}</div>}
              </div>
            </div>
            {address && <p className="text-sm text-[#4B5563] mt-4">{address}</p>}
          </div>
          <div className="card-flat p-6">
            <div className="label-eyebrow text-[#4B5563]">Tip</div>
            <p className="text-sm text-[#111827] mt-2 leading-relaxed">A clear photo + full name boost click-through rates by ~30%. Customers trust links from people they can see.</p>
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}
